import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

// No hardcoded data needed anymore - using database only

interface ChecklistItem {
  id: string;
  target: boolean;
  ready: boolean;
  insert: boolean;
  item: string;
  comment: string;
}

interface Section {
  id: string;
  name: string;
  phases: Array<{ id: string; title: string; isPlaceholder?: boolean }>;
}

interface PreloadedSection {
  id: string;
  section_name: string;
  phases: Array<{
    id: string;
    phase_title: string;
    items: Array<{
      id: string;
      item_text: string;
      notes: string | null;
      is_ready: boolean;
      is_bundled: boolean;
      is_secured: boolean;
    }>;
  }>;
}

interface PreloadedTemplate {
  id: string;
  template_name: string;
  phases: Array<{
    id: string;
    phase_title: string;
    items: Array<{
      id: string;
      item_text: string;
      notes: string | null;
      is_ready: boolean;
      is_bundled: boolean;
      is_secured: boolean;
    }>;
  }>;
}

interface SearchResult {
  sectionId: string;
  sectionName: string;
  phaseId: string;
  phaseTitle: string;
  item: ChecklistItem;
  phaseTitles: Record<string, string>;
  isTemplate?: boolean;
  matchedFields: string[]; // Track which fields matched the search
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: Section[];
  userSections: PreloadedSection[];
  templates: PreloadedTemplate[];
  phaseTitles: Record<string, string>;
  onResultSelect: (sectionId: string, phaseId: string, phaseTitle: string, isTemplate?: boolean) => void;
}

export const SearchModal = ({ isOpen, onClose, sections, userSections, templates, phaseTitles, onResultSelect }: SearchModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [templateResults, setTemplateResults] = useState<SearchResult[]>([]);
  const [userResults, setUserResults] = useState<SearchResult[]>([]);
  const { user } = useAuth();
  
  // Memoize guest data to prevent infinite loops
  const guestSection = useMemo(() => {
    if (user) return null;
    const data = sessionStorage.getItem('guest-checklist-section');
    return data ? JSON.parse(data) : null;
  }, [user, isOpen]);
  
  const guestPhases = useMemo(() => {
    if (user) return [];
    const data = sessionStorage.getItem('guest-checklist-phases');
    return data ? JSON.parse(data) : [];
  }, [user, isOpen]);


  const searchResults = useMemo(() => {
    if (searchTerm.length < 2) return { templateResults: [], userResults: [] };

    const templateResults: SearchResult[] = [];
    const userResults: SearchResult[] = [];
    const searchLower = searchTerm.toLowerCase();
    
    // Search guest data if exists
    if (!user && guestSection && guestPhases.length > 0) {
      guestPhases.forEach((phase: any) => {
        const itemsKey = `checklist-items-${phase.id}`;
        const itemsData = sessionStorage.getItem(itemsKey);
        
        if (itemsData) {
          const items = JSON.parse(itemsData);
          items.forEach((item: any) => {
            const matchedFields: string[] = [];
            
            if (item.item && item.item.toLowerCase().includes(searchLower)) {
              matchedFields.push('Item');
            }
            if (item.comment && item.comment.toLowerCase().includes(searchLower)) {
              matchedFields.push('Comment');
            }
            
            if (matchedFields.length > 0) {
              const checklistItem: ChecklistItem = {
                id: item.id,
                target: item.target || false,
                ready: item.ready || false,
                insert: item.insert || false,
                item: item.item || '',
                comment: item.comment || ''
              };
              
              userResults.push({
                sectionId: guestSection.id,
                sectionName: guestSection.name,
                phaseId: phase.id,
                phaseTitle: phase.title,
                item: checklistItem,
                phaseTitles: { [phase.id]: phase.title },
                isTemplate: false,
                matchedFields: matchedFields
              });
            }
          });
        }
      });
    }
    
    // Search through templates (sample checklists)
    templates.forEach(template => {
      template.phases.forEach(phase => {
        const phaseTitle = phase.phase_title;
        
        phase.items.forEach(item => {
          // Skip empty items
          if (!item.item_text || item.item_text.trim() === '') return;
          
          const itemMatches = item.item_text.toLowerCase().includes(searchLower);
          const commentMatches = item.notes && item.notes.toLowerCase().includes(searchLower);
          const phaseMatches = phaseTitle.toLowerCase().includes(searchLower);
          const templateMatches = template.template_name.toLowerCase().includes(searchLower);

          if (itemMatches || commentMatches || phaseMatches || templateMatches) {
            const matchedFields: string[] = [];
            if (itemMatches) matchedFields.push('Item');
            if (commentMatches) matchedFields.push('Comment');
            if (phaseMatches) matchedFields.push('Phase');
            if (templateMatches) matchedFields.push('Event');

            const checklistItem: ChecklistItem = {
              id: item.id,
              target: item.is_secured,
              ready: item.is_ready,
              insert: item.is_bundled,
              item: item.item_text,
              comment: item.notes || ''
            };

            templateResults.push({
              sectionId: template.id,
              sectionName: template.template_name,
              phaseId: phase.id,
              phaseTitle,
              item: checklistItem,
              phaseTitles: { [phase.id]: phaseTitle },
              isTemplate: true,
              matchedFields
            });
          }
        });
      });
    });
    
    // Search through user sections from database
    userSections.forEach(section => {
      section.phases.forEach(phase => {
        const phaseTitle = phase.phase_title;
        
        phase.items.forEach(item => {
          // Skip empty items
          if (!item.item_text || item.item_text.trim() === '') return;
          
          const itemMatches = item.item_text.toLowerCase().includes(searchLower);
          const commentMatches = item.notes && item.notes.toLowerCase().includes(searchLower);
          const phaseMatches = phaseTitle.toLowerCase().includes(searchLower);
          const sectionMatches = section.section_name.toLowerCase().includes(searchLower);

          if (itemMatches || commentMatches || phaseMatches || sectionMatches) {
            const matchedFields: string[] = [];
            if (itemMatches) matchedFields.push('Item');
            if (commentMatches) matchedFields.push('Comment');
            if (phaseMatches) matchedFields.push('Phase');
            if (sectionMatches) matchedFields.push('Event');

            const checklistItem: ChecklistItem = {
              id: item.id,
              target: item.is_secured,
              ready: item.is_ready,
              insert: item.is_bundled,
              item: item.item_text,
              comment: item.notes || ''
            };

            userResults.push({
              sectionId: section.id,
              sectionName: section.section_name,
              phaseId: phase.id,
              phaseTitle,
              item: checklistItem,
              phaseTitles: { [phase.id]: phaseTitle },
              isTemplate: false,
              matchedFields
            });
          }
        });
      });
    });

    return { templateResults, userResults };
  }, [searchTerm, userSections, templates, user, guestSection, guestPhases]);

  useEffect(() => {
    setTemplateResults(searchResults.templateResults);
    setUserResults(searchResults.userResults);
  }, [searchResults]);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTemplateResults([]);
      setUserResults([]);
    }
  }, [isOpen]);

  const handleResultClick = (result: SearchResult) => {
    onResultSelect(result.sectionId, result.phaseId, result.phaseTitle, result.isTemplate);
    onClose();
  };

  const highlightMatch = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-primary/20 text-primary rounded px-1">
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] md:max-h-[80vh]" aria-describedby="search-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Checklists
          </DialogTitle>
        </DialogHeader>
        <p id="search-description" className="sr-only">
          Search through your triathlon checklist items, comments, phases, and events to quickly find what you need.
        </p>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search items, comments, phases, or events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              autoFocus
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearchTerm('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto space-y-4">
            {searchTerm.length < 2 ? (
              <div className="text-center text-muted-foreground py-8">
                Type at least 2 characters to search
              </div>
            ) : templateResults.length === 0 && userResults.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No results found for "{searchTerm}"
              </div>
            ) : (
              <>
                {templateResults.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-normal text-muted-foreground px-1">
                      Sample Checklists ({templateResults.length})
                    </h3>
                    <div className="space-y-2">
                      {templateResults.map((result, index) => (
                        <div
                          key={`template-${result.phaseId}-${result.item.id}-${index}`}
                          className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => handleResultClick(result)}
                        >
                           <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="space-y-1">
                                {/* Always show section and phase context */}
                                <div className="text-xs text-muted-foreground">
                                  {result.sectionName} → {result.phaseTitle}
                                </div>
                                {/* Show matched fields with labels */}
                                 {result.matchedFields.includes('Item') && (
                                  <div className="font-normal text-sm">
                                    <span className="text-muted-foreground">Item: </span>
                                    {highlightMatch(result.item.item, searchTerm)}
                                  </div>
                                )}
                                {result.matchedFields.includes('Comment') && result.item.comment && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">Notes: </span>
                                    {highlightMatch(result.item.comment, searchTerm)}
                                  </div>
                                )}
                                {result.matchedFields.includes('Phase') && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">Phase: </span>
                                    {highlightMatch(result.phaseTitle, searchTerm)}
                                  </div>
                                )}
                                {result.matchedFields.includes('Event') && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">Event: </span>
                                    {highlightMatch(result.sectionName, searchTerm)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {result.item.target && (
                                <div className="w-2 h-2 bg-primary rounded-full" title="Target" />
                              )}
                              {result.item.ready && (
                                <div className="w-2 h-2 bg-secondary rounded-full" title="Ready" />
                              )}
                              {result.item.insert && (
                                <div className="w-2 h-2 bg-tertiary rounded-full" title="Insert" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {userResults.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-normal text-muted-foreground px-1">
                      {!user ? 'Your Draft Checklist' : 'Your Checklists'} ({userResults.length})
                    </h3>
                    <div className="space-y-2">
                      {userResults.map((result, index) => (
                        <div
                          key={`user-${result.phaseId}-${result.item.id}-${index}`}
                          className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => handleResultClick(result)}
                        >
                           <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="space-y-1">
                                {/* Always show section and phase context */}
                                <div className="text-xs text-muted-foreground">
                                  {result.sectionName} → {result.phaseTitle}
                                </div>
                                {/* Show matched fields with labels */}
                                 {result.matchedFields.includes('Item') && (
                                  <div className="font-normal text-sm">
                                    <span className="text-muted-foreground">Item: </span>
                                    {highlightMatch(result.item.item, searchTerm)}
                                  </div>
                                )}
                                {result.matchedFields.includes('Comment') && result.item.comment && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">Notes: </span>
                                    {highlightMatch(result.item.comment, searchTerm)}
                                  </div>
                                )}
                                {result.matchedFields.includes('Phase') && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">Phase: </span>
                                    {highlightMatch(result.phaseTitle, searchTerm)}
                                  </div>
                                )}
                                {result.matchedFields.includes('Event') && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">Event: </span>
                                    {highlightMatch(result.sectionName, searchTerm)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {result.item.target && (
                                <div className="w-2 h-2 bg-primary rounded-full" title="Target" />
                              )}
                              {result.item.ready && (
                                <div className="w-2 h-2 bg-secondary rounded-full" title="Ready" />
                              )}
                              {result.item.insert && (
                                <div className="w-2 h-2 bg-tertiary rounded-full" title="Insert" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};